import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ImageOff, ArrowRight, Video } from "lucide-react";
import { api, buildWhatsAppLink } from "../lib/api";
import { APPOINTMENT_TEXT } from "../lib/home-content";

const CATEGORY_TABS = [
  "All",
  "Designer Suits",
  "Co-Ord Sets",
  "Kaftans",
  "Kurta Sets",
  "Indo-Western Wear",
  "Festive Collections",
];

const Collections = () => {
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "All";
  const [active, setActive] = useState(initial);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api
      .get("/products")
      .then(({ data }) => on && setProducts(data || []))
      .finally(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  useEffect(() => {
    setActive(params.get("category") || "All");
  }, [params]);

  const filtered = useMemo(() => {
    if (active === "All") return products;
    return products.filter((p) => p.category === active);
  }, [products, active]);

  const counts = useMemo(() => {
    const map = { All: products.length };
    for (const p of products) map[p.category] = (map[p.category] || 0) + 1;
    return map;
  }, [products]);

  const handleTab = (cat) => {
    if (cat === "All") {
      params.delete("category");
    } else {
      params.set("category", cat);
    }
    setParams(params, { replace: true });
  };

  return (
    <div data-testid="collections-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-12">
        <div className="overline">Lookbook · S/S 26</div>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[96px] font-light leading-[1.0] mt-4 max-w-4xl">
          The <span className="italic text-[#4A3FCF]">collections</span>, line-sheet ready.
        </h1>
        <p className="mt-6 text-[15px] text-[#5A5F7A] max-w-2xl leading-relaxed">
          Curated by our in-house design team. Every piece is sample-approved and available for bulk wholesale. MOQ from 6 pieces.
        </p>
      </section>

      <section className="border-y border-[#DCE4F2] sticky top-[73px] z-30 bg-[#F4F7FE]/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-4 flex flex-wrap items-center gap-2">
          {CATEGORY_TABS.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => handleTab(c)}
              className={`text-[11px] tracking-[0.22em] uppercase font-medium px-4 py-2 border transition-colors ${
                active === c
                  ? "bg-[#0F1245] text-white border-[#0F1245]"
                  : "bg-transparent text-[#0F1245] border-[#DCE4F2] hover:border-[#0F1245]"
              }`}
            >
              {c} <span className="opacity-50 ml-1">{counts[c] != null ? `(${counts[c]})` : ""}</span>
            </button>
          ))}
          <div className="ml-auto flex gap-3">
            <a href={buildCatalogueUrl()} target="_blank" rel="noreferrer" data-testid="collections-pdf-btn" className="btn-secondary !py-2 !px-4 text-[10px]">
              PDF Catalogue
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-[#EFE9DF]" />
                <div className="h-4 bg-[#EFE9DF] mt-5 w-2/3" />
                <div className="h-3 bg-[#EFE9DF] mt-3 w-1/3" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-[#DCE4F2]">
            <p className="font-serif-display text-3xl italic text-[#0F1245]/50">No pieces in this vertical yet.</p>
            <p className="text-[14px] text-[#5A5F7A] mt-3">Request the catalogue on WhatsApp to see the latest line-sheet.</p>
            <a href={buildWhatsAppLink(`Hi, I'd like the latest ${active} line-sheet from Style India Delhi.`)} target="_blank" rel="noreferrer" data-testid="empty-whatsapp-btn" className="btn-primary mt-8">
              WhatsApp Us
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {filtered.map((p, i) => (
              <article key={p.id} data-testid={`product-card-${i}`} className="group">
                <div className="aspect-[3/4] image-placeholder relative overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <ImageOff size={26} strokeWidth={1.2} className="text-[#4A3FCF]/70" />
                      <span className="mt-3 font-serif-display italic text-[#0F1245]/40">Sample on request</span>
                    </div>
                  )}
                  <span className="absolute top-4 left-4 bg-[#F4F7FE]/90 px-3 py-1 text-[10px] tracking-[0.22em] uppercase">{p.category}</span>
                </div>
                <div className="pt-5">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif-display text-2xl text-[#0F1245] leading-tight">{p.name}</h3>
                    <span className="text-[12px] tracking-widest uppercase text-[#4A3FCF] whitespace-nowrap pt-1">MOQ {p.moq}</span>
                  </div>
                  {p.description && <p className="text-[13.5px] text-[#5A5F7A] mt-2 leading-relaxed">{p.description}</p>}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#DCE4F2]">
                    <div className="text-[13px] text-[#0F1245]">
                      <span className="font-serif-display text-xl">₹ {Number(p.price || 0).toLocaleString("en-IN")}</span>
                      <span className="text-[#5A5F7A] ml-1 text-[12px]">/ piece</span>
                    </div>
                    <a
                      href={buildWhatsAppLink(`Hi Style India Delhi, I'd like to enquire about "${p.name}" (MOQ ${p.moq}).`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`product-enquire-${i}`}
                      className="text-[11px] tracking-[0.22em] uppercase font-medium hover:text-[#4A3FCF] transition-colors inline-flex items-center gap-2"
                    >
                      Enquire <ArrowRight size={14} />
                    </a>
                  </div>
                  {(p.fabric || p.sizes) && (
                    <div className="mt-3 flex gap-4 text-[11px] text-[#5A5F7A] uppercase tracking-wider">
                      {p.fabric && <span>{p.fabric}</span>}
                      {p.sizes && <span>· {p.sizes}</span>}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="bg-[#0F1245] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="overline" style={{ color: "#B7E4F4" }}>Bulk Wholesale</div>
            <h3 className="font-serif-display text-3xl md:text-4xl font-light mt-3">Want the full season line-sheet?</h3>
          </div>
          <div className="flex gap-4">
            <Link to="/#contact" data-testid="collections-cta-contact" className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-[#0F1245]">Get in touch</Link>
            <a href={buildWhatsAppLink(APPOINTMENT_TEXT)} target="_blank" rel="noreferrer" data-testid="collections-cta-appointment" className="btn-primary !bg-white !text-[#0F1245] !border-white hover:!bg-[#B7E4F4] hover:!border-[#B7E4F4]">
              <Video size={14} strokeWidth={1.5} /> Book Appointment
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Collections;
